import * as React from 'react';
import videojs from 'video.js';
import { css, StyleSheet } from 'aphrodite';
import { ZIndex } from '../constants';
import VjsVideoSideSuper from './components/VjsVideoSideSuper';
import VjsResult from './components/VjsResult';
import VjsVideoController from './components/VjsVideoController';
import VjsFullScreenButton from './components/VjsFullScreenButton';
import VjsAutoPlayButton from './components/VjsAutoPlayButton';
import VjsCompleteButton from './components/VjsCompleteButton';
const VJSComponent = videojs.getComponent('Component');
// 動画プレイヤー内の左上のタイトル領域
VJSComponent.registerComponent('VjsVideoSideSuper', VjsVideoSideSuper);
// 動画視聴完了後のUI
VJSComponent.registerComponent('VjsResult', VjsResult);
// 動画の再生状態のオリジナルコントローラー
VJSComponent.registerComponent('VjsVideoController', VjsVideoController);
// 動画プレイヤー内の「全画面」ボタン
VJSComponent.registerComponent('VjsFullScreenButton', VjsFullScreenButton);
// 動画プレイヤー内の「自動再生切り替え」ボタン
VJSComponent.registerComponent('VjsAutoPlayButton', VjsAutoPlayButton);
// 動画プレイヤー内の「完了」ボタン（エンディング開始秒に到達したら表示）
VJSComponent.registerComponent('VjsCompleteButton', VjsCompleteButton);
const VJSCompomnent_Z_INDEX = 0; // 動画プレイヤー内のコンポーネントの z-index. 被らないので全部0で統一する
export const LBPlayer = ({ videoOptions, resultOptions, operationOptions, getVideoRef, getPlayer, }) => {
    const [ref, setRef] = React.useState(null);
    const [player, setPlayer] = React.useState(null);
    const [renderedResult, setRenderedResult] = React.useState(false);
    const [renderedExitFullScreen, setRenderedExitFullScreen] = React.useState(false);
    const [renderedFullScreenButton, setRenderedFullScreenButton] = React.useState(false);
    const [renderedVideoController, setRenderedVideoController] = React.useState(false);
    const [renderedAutoPlayButton, setRenderedAutoPlayButton] = React.useState(false);
    const [renderedCompleteButton, setRenderedCompleteButton] = React.useState(false);
    React.useEffect(() => {
        if (!ref) {
            return;
        }
        setPlayer(videojs(ref, Object.assign({}, videoOptions)));
        hookChildren();
        if (getVideoRef) {
            getVideoRef(ref);
        }
        getPlayer(player);
        if (!player) {
            return;
        }
        setEventToComponent();
    }, [ref, player]);
    React.useEffect(() => {
        return () => {
            var _a, _b;
            (_b = (_a = resultOptions === null || resultOptions === void 0 ? void 0 : resultOptions.vm) === null || _a === void 0 ? void 0 : _a.onUnMount) === null || _b === void 0 ? void 0 : _b.call(_a);
        };
    }, []);
    const hookResult = () => {
        if (!player) {
            return;
        }
        if (!resultOptions) {
            return;
        }
        if (renderedResult) {
            return;
        }
        // 視聴後のセクション
        player.addChild('VjsResult', {
            resultOptions: resultOptions,
            onClickExit: operationOptions.onClickExitInnerFullScreen,
        }, VJSCompomnent_Z_INDEX);
        setRenderedResult(true);
    };
    const hookExitFullscreen = () => {
        if (!operationOptions.showBackButton) {
            return;
        }
        if (!player) {
            return;
        }
        if (renderedExitFullScreen) {
            return;
        }
        // 左上タイトル領域
        player.addChild('VjsVideoSideSuper', {
            onClickExit: operationOptions.onClickExitInnerFullScreen,
        }, VJSCompomnent_Z_INDEX);
        setRenderedExitFullScreen(true);
    };
    const hookFullscreenButton = () => {
        if (!player) {
            return;
        }
        if (renderedFullScreenButton) {
            return;
        }
        player.addChild('VjsFullScreenButton', {}, 0);
        setRenderedFullScreenButton(true);
    };
    const hookVideoController = () => {
        if (!player) {
            return;
        }
        if (renderedVideoController) {
            return;
        }
        player.addChild('VjsVideoController', {
            videoJsOptions: videoOptions,
            player: player,
            isFirstLesson: operationOptions.isFirstLesson,
            isLastLesson: operationOptions.isLastLesson,
            onClickPrev: operationOptions.onClickPrev,
            onClickNext: operationOptions.onClickNext,
            isAlwaysShowStepButtons: operationOptions.isAlwaysShowStepButtons,
        }, 6);
        setRenderedVideoController(true);
    };
    const hookAutoPlayButton = () => {
        if (!operationOptions.showAutoplayToggle) {
            return;
        }
        if (!player) {
            return;
        }
        if (renderedAutoPlayButton) {
            return;
        }
        player.addChild('VjsAutoPlayButton', {
            apiClient: operationOptions.apiClient,
        }, VJSCompomnent_Z_INDEX);
        setRenderedAutoPlayButton(true);
    };
    const hookCompleteButton = () => {
        if (!operationOptions.completeButtonVM || !operationOptions.onClickComplete) {
            return;
        }
        if (!player) {
            return;
        }
        if (renderedCompleteButton) {
            return;
        }
        player.addChild('VjsCompleteButton', {
            vm: operationOptions.completeButtonVM,
            onClickComplete: operationOptions.onClickComplete,
        }, VJSCompomnent_Z_INDEX);
        setRenderedCompleteButton(true);
    };
    const hookChildren = () => {
        hookResult();
        hookExitFullscreen();
        hookFullscreenButton();
        hookVideoController();
        hookAutoPlayButton();
        hookCompleteButton();
    };
    const setEventToComponent = () => {
        const ControlBar = player.getChild('ControlBar');
        // プログレスバーの表示
        ControlBar.on('showProgressBar', (event) => {
            var _a, _b, _c;
            event.target.classList.add('vjs-show-progress-bar');
            (_a = document.getElementsByClassName('vjs-fullscreen-control')[0]) === null || _a === void 0 ? void 0 : _a.classList.add('show-fullscreen-control');
            (_b = document.getElementsByClassName('vjs-video-side-super')[0]) === null || _b === void 0 ? void 0 : _b.classList.add('show-video-side-super');
            (_c = document.getElementsByClassName('vjs-auto-play-button')[0]) === null || _c === void 0 ? void 0 : _c.classList.add('show-auto-play-button');
        });
        // プログレスバーの非表示
        ControlBar.on('hideProgressBar', (event) => {
            var _a, _b, _c;
            event.target.classList.remove('vjs-show-progress-bar');
            (_a = document.getElementsByClassName('vjs-fullscreen-control')[0]) === null || _a === void 0 ? void 0 : _a.classList.remove('show-fullscreen-control');
            (_b = document.getElementsByClassName('vjs-video-side-super')[0]) === null || _b === void 0 ? void 0 : _b.classList.remove('show-video-side-super');
            (_c = document.getElementsByClassName('vjs-auto-play-button')[0]) === null || _c === void 0 ? void 0 : _c.classList.remove('show-auto-play-button');
        });
    };
    return (React.createElement("div", { "data-vjs-player": true },
        React.createElement("video", { ref: (node) => setRef(node), className: ['video-js', css(styles.container)].join(' ') })));
};
const styles = StyleSheet.create({
    container: {
        width: '100%',
        height: '100%',
        position: 'relative',
        zIndex: ZIndex.VideoPlayer,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
    },
});
